import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CollectionPoint,
  CollectionPointSearchParams,
  CollectionPointWithI18nKey
} from '../../../../types/collectionPoints';
import { joinZipCodeCity } from '../../../../utils/address';
import callApi from '../../../../utils/callApi';
import { DeliveryOption, LOADING_TYPES, useCheckoutState } from '../../../../utils/checkoutState';
import { config } from '../../../../utils/config';
import { geocode } from '../../../../utils/geocode';
import { getSearchParamsFromAddress, getSearchParamsFromCurrentPosition } from '../../../../utils/googleMaps';
import { pushErrorTrackingEventFromErrorObject, pushErrorTrackingEventFromI18nKey } from '../../../../utils/tracking';
import LoadingIndicator from '../../../atoms/LoadingIndicator';
import { showNotification } from '../../Notifications';
import LocationDetails from '../LocationDetails';
import AutocompleteSearchSection from './AutocompleteSearchSection';
import Dropdown from './Dropdown';
import List from './List';
import Map from './Map';
import SearchSection from './SearchSection';
import ViewOptions, { ViewOption } from './ViewOptions';
import getCollectionPointI18nKey from './getCollectionPointI18nKey';

const getCollectionPointTypes = (availableDeliveryOptions: DeliveryOption[] | undefined) =>
  (availableDeliveryOptions ?? [])
    .map(({ collectionPointType, carrierGroupKey }) => ({ collectionPointType, carrierGroupKey }))
    .filter(({ collectionPointType }) => collectionPointType !== 'generic');

type SearchPageProps = {
  searchParams: CollectionPointSearchParams;
  setSearchParams: (params: CollectionPointSearchParams) => void;
  results: CollectionPointWithI18nKey[];
  setResults: (locations: CollectionPointWithI18nKey[]) => void;
  onSelect: (id: string) => void;
  view: ViewOption;
  setView: (view: ViewOption) => void;
  isStorePickup: boolean;
  isGoogleMapsSdkLoaded: boolean;
  isDetailView: boolean;
  onBackButtonClick: () => void;
};

const Overview = ({ setSearchParams, setResults, onSelect, setView, onBackButtonClick, ...rest }: SearchPageProps) => {
  const { searchParams, results, view, isStorePickup, isGoogleMapsSdkLoaded, isDetailView } = rest;
  const [isEditing, setEditing] = useState(!searchParams.streetHouseNumber);
  const { checkoutState, isLoading, setLoading } = useCheckoutState();
  const { formatMessage } = useIntl();

  const availableDeliveryOptions = checkoutState.orderOptions?.shipping?.collectionPointDelivery;
  const useAutocompleteComponent = config.collectionPoints.googleMapsAutocomplete.enabled;

  const noCollectionPointsFoundNotificationMessage =
    useAutocompleteComponent ?
      'shipping.notifications.noCollectionPointsFoundAutocomplete'
    : 'shipping.notifications.noCollectionPointsFound';

  const availableFilters =
    !results.length || isStorePickup ?
      []
    : getCollectionPointTypes(availableDeliveryOptions)?.map(deliveryOption => ({
        i18nKey: getCollectionPointI18nKey(deliveryOption),
        collectionPointType: deliveryOption.collectionPointType!
      }));

  const onSearch = async (_searchParams: CollectionPointSearchParams) => {
    const filter = _searchParams.type;
    setLoading(filter ? LOADING_TYPES.collectionPointFilterSearch(filter) : LOADING_TYPES.collectionPointSearch);
    setSearchParams(_searchParams);
    const { streetHouseNumber, zipCode, city, maximumDistance, type, state } = _searchParams;
    const cityZipCode = joinZipCodeCity({ zipCode, city, country: { iso2Code: config.country } });

    try {
      const coordinates = _searchParams.coordinates ?? (await geocode([streetHouseNumber, cityZipCode].join(', ')));
      const collectionPointSearchResponse = await callApi({
        endpoint: '/collection-points',
        urlParams: {
          streetHouseNumber,
          zipCode,
          city,
          countryCode: config.country,
          ...(state && {state}),
          latitude: coordinates.latitude.toString(),
          longitude: coordinates.longitude.toString(),
          maximumDistance: maximumDistance.toString(),
          maximumResults: '10',
          type:
            type ? type
            : isStorePickup ? 'generic'
            : getCollectionPointTypes(availableDeliveryOptions)
                .map(({ collectionPointType }) => collectionPointType)
                .join(',')
        }
      });
      if (!collectionPointSearchResponse.items) {
        setLoading(LOADING_TYPES.off);
        showNotification({
          level: 'warning',
          text: formatMessage({ id: noCollectionPointsFoundNotificationMessage }),
          autoClose: true
        });
        return;
      }
      setResults(
        collectionPointSearchResponse.items.map((location: CollectionPoint) => ({
          ...location,
          i18nKey: getCollectionPointI18nKey(location.deliveryOption)
        }))
      );
      setEditing(false);
      setLoading(LOADING_TYPES.off);
    } catch (e: any) {
      setLoading(LOADING_TYPES.off);

      setResults([]);
      const errorI18nKey = {
        404: noCollectionPointsFoundNotificationMessage,
        422: 'shipping.notifications.validationError'
      }[e.statusCode as number];
      if (errorI18nKey) {
        showNotification({ level: 'warning', text: formatMessage({ id: errorI18nKey }), autoClose: true });
        pushErrorTrackingEventFromI18nKey(errorI18nKey, 'collection_points');
      } else {
        console.error(e);
        showNotification({
          level: 'warning',
          text: formatMessage({ id: 'shipping.notifications.incompleteAddressComponents' }),
          autoClose: true
        });
        pushErrorTrackingEventFromErrorObject(e, 'collection_points');
      }
    }
  };

  const existingShippingAddress = checkoutState.orderState.addresses?.shipping;

  const dropdown = (
    <Dropdown
      isDisabled={!!isLoading && isLoading !== LOADING_TYPES.collectionPointSearch}
      lastUsedAddress={existingShippingAddress}
      onMyLocation={async () => {
        setLoading(LOADING_TYPES.collectionPointSearch);
        const _searchParams = await getSearchParamsFromCurrentPosition();
        if (!_searchParams) {
          showNotification({
            level: 'warning',
            text: formatMessage({ id: 'shipping.notifications.myLocationUnavailable' }),
            autoClose: true
          });
          setLoading(LOADING_TYPES.off);
          return;
        }
        onSearch(_searchParams);
      }}
      onLastUsedAddress={() => onSearch(getSearchParamsFromAddress(existingShippingAddress!))}
    />
  );

  const onClearSearch = () => {
    setSearchParams({
      streetHouseNumber: '',
      zipCode: '',
      city: '',
      maximumDistance: 0,
      formattedAddress: '',
      type: ''
    });
    setResults([]);
  };

  const searchComponent =
    useAutocompleteComponent ?
      <AutocompleteSearchSection
        onSearch={onSearch}
        onClearSearch={onClearSearch}
        searchParams={searchParams}
        initialValues={searchParams}
        isLoading={isLoading}
        isGoogleMapsSdkLoaded={isGoogleMapsSdkLoaded}
        dropdown={dropdown}
        availableFilters={availableFilters}
      />
    : <SearchSection
        onSearch={onSearch}
        onSearchEdit={() => setEditing(true)}
        isEditing={isEditing}
        availableFilters={availableFilters}
        initialValues={searchParams}
        isLoading={isLoading}
        dropdown={dropdown}
      />;

  return (
    <>
      <div className={classNames('overview', { 'overview--with-search': !isDetailView })}>
        {isDetailView ?
          <LocationDetails
            location={results![0]}
            view={view}
            onBackButtonClick={onBackButtonClick}
            isDisabled={!!isLoading && isLoading !== LOADING_TYPES.collectionPointSearch}
          />
        : searchComponent}
      </div>
      {isGoogleMapsSdkLoaded ?
        <Map locations={results} onMarkerClick={onSelect} viewOption={isDetailView ? 'detail' : view} />
      : <div className="map">
          <LoadingIndicator />
        </div>
      }
      {!isDetailView && (
        <>
          <List locations={results} onItemClick={onSelect} isLoading={isLoading} />
          <ViewOptions value={view} onChange={setView} isLoading={isLoading} />
        </>
      )}
    </>
  );
};
export default Overview;
